import { buildScheduleAndWindowDateFields, buildModifiedByFieldsFromUserSession } from "../commonUtils";
import { getISODateTimeString } from "../dateTimeFormatter";
import { getUnionContractGivenWorkgroup, getWorkgroupGivenJobCode } from "../workgroupMapper";
import { BID_STATUS, BID_TYPE, BID_UPDATE_TYPE } from "./bidConstants";

export function buildBidEvent(data, s3ObjectName = undefined, userSession = undefined) {
  const newBidEvent = {
    ...(data.awardLog ? { awardLog: data.awardLog } : {}),
    bidName: data.bidName ? data.bidName : "",
    bidType: data.bidType,
    ...(data.comments ? { comments: data.comments } : {}),
    ...(data.laborContact ? { laborContact: data.laborContact } : {}),
    eventType: data.eventType,
    ...(data.referenceNumber ? { referenceNumber: data.referenceNumber } : {}),
    scheduledStatus: data.scheduledStatus,
    station: data.station,
    ...(data.stationTier ? { stationTier: parseInt(data.stationTier, 10) } : {}),
    status: data.status,
    ...(data.v ? { v: parseInt(data.v) } : {}),
    workgroup: data.workgroup,
    ...(s3ObjectName ? { s3ObjectName: decodeURIComponent(s3ObjectName) } : {}),
    unionContract: getUnionContractGivenWorkgroup(data.workgroup),
    ...buildModifiedByFieldsFromUserSession(userSession),
    ...buildScheduleAndWindowDateFields(data),
  };
  return newBidEvent;
}

export function buildBidS3Data(userSession, bidMetadata, bidLines, bidGroup, s3ObjectName) {
  let bidS3Data = {
    bidMetadata: buildBidEvent(bidMetadata, s3ObjectName),
    bidLines: isWithdrawn(bidMetadata.status) ? removeBidLineAwardData(bidLines) : bidLines,
    bidGroup: isWithdrawn(bidMetadata.status)
      ? removeBidGroupEmpAwardData(bidGroup)
      : bidGroup.map((employee) => ({ ...employee, isExpanded: false })),
    modifiedBy: {
      eid: userSession.eid,
      firstName: userSession.firstName,
      lastName: userSession.lastName,
      workgroup: getWorkgroupGivenJobCode(userSession.jobCode),
    },
    referenceNumber: bidMetadata.referenceNumber ? bidMetadata.referenceNumber : "",
  };
  return bidS3Data;
}

export function buildBidChoiceEvent(data, userSession, comments) {
  const newBidChoiceEvent = {
    bidType: data.bidType,
    eid: data.eid,
    modifiedByEid: userSession.eid,
    modifiedByFirstName: userSession.firstName,
    modifiedByLastName: userSession.lastName,
    modifiedByWorkgroup: getWorkgroupGivenJobCode(userSession.jobCode),
    choices: data.choices,
    ...(data.datCount ? { datCount: data.datCount } : {}),
    ...(data.ftoCount ? { ftoCount: data.ftoCount } : {}),
    ...(data.gocHolCount ? { gocHolCount: data.gocHolCount } : {}),
    ...(data.ptoCount ? { ptoCount: data.ptoCount } : {}),
    referenceNumber: data.referenceNumber,
    ...(data.schedEndDate ? { schedEndDate: getISODateTimeString(data.schedEndDate) } : {}),
    ...(data.schedStartDate ? { schedStartDate: getISODateTimeString(data.schedStartDate) } : {}),
    ...(data.schedMonth ? { schedMonth: data.schedMonth } : {}),
    ...(data.schedYear ? { schedYear: data.schedYear } : {}),
    station: data.station,
    ...(data.stationTier ? { stationTier: parseInt(data.stationTier, 10) } : {}),
    status: data.status,
    v: data.v,
    ...(data.windowEndDate ? { windowEndDate: getISODateTimeString(data.windowEndDate) } : {}),
    ...(data.windowStartDate ? { windowStartDate: getISODateTimeString(data.windowStartDate) } : {}),
    workgroup: data.workgroup,
    ftpt: data.ftpt,
    isEligible: data.isEligible,
    updateType: data.updateType,
    ...(comments ? { comments: comments } : {}),
  };
  return newBidChoiceEvent;
}

export function prepareNewBidChoiceEventPayload({
  bidChoiceData,
  selectedChoices,
  ftoCount,
  datCount,
  ptoCount,
  gocHolCount,
}) {
  const newBidChoiceEvent = {
    bidType: bidChoiceData.bidType,
    eid: bidChoiceData.eid,
    choices: selectedChoices,
    ...(ftoCount ? { ftoCount: ftoCount } : {}),
    ...(datCount ? { datCount: datCount } : {}),
    ...(ptoCount ? { ptoCount: ptoCount } : {}),
    ...(gocHolCount ? { gocHolCount: gocHolCount } : {}),
    referenceNumber: bidChoiceData.referenceNumber,
    schedEndDate: bidChoiceData.schedEndDate,
    schedStartDate: bidChoiceData.schedStartDate,
    ...(bidChoiceData.schedMonth ? { schedMonth: bidChoiceData.schedMonth } : {}),
    ...(bidChoiceData.schedYear ? { schedYear: bidChoiceData.schedYear } : {}),
    station: bidChoiceData.station,
    ...(bidChoiceData.stationTier ? { stationTier: parseInt(bidChoiceData.stationTier, 10) } : {}),
    status: bidChoiceData.status,
    v: bidChoiceData.v,
    windowEndDate: bidChoiceData.windowEndDate,
    windowStartDate: bidChoiceData.windowStartDate,
    workgroup: bidChoiceData.workgroup,
    ftpt: bidChoiceData.ftpt,
    isEligible: bidChoiceData.isEligible,
    updateType: BID_UPDATE_TYPE.CREATE,
  };

  return newBidChoiceEvent;
}

const isWithdrawn = (status) => {
  return status === BID_STATUS.WITHDRAWN;
};

const removeBidLineAwardData = (bidLines) => {
  let updatedLines = [];
  for (const bidLine of bidLines) {
    if (bidLine.isDupedLine) {
      // Don't process duped lines (This effectively removes them)
      continue;
    }
    let line = JSON.parse(JSON.stringify(bidLine));
    if ("awardedEid" in line) {
      delete line.awardedEid;
    }
    if ("awardedShortName" in line) {
      delete line.awardedShortName;
    }
    if ("awardedLastName" in line) {
      delete line.awardedLastName;
    }
    if ("awardedRank" in line) {
      delete line.awardedRank;
    }
    if ("awardType" in line) {
      delete line.awardType;
    }
    updatedLines.push(line);
  }
  return updatedLines;
};

const removeBidGroupEmpAwardData = (bidGroup) => {
  let updatedGroup = [];
  for (const emp of bidGroup) {
    let employee = JSON.parse(JSON.stringify(emp));
    if ("awardedLine" in employee) {
      delete employee.awardedLine;
    }
    if ("awardType" in employee) {
      delete employee.awardType;
    }
    employee.isExpanded = false;
    updatedGroup.push(employee);
  }
  return updatedGroup;
};

export function buildTimeOffBidS3Data(userSession, bidMetadata, bidAllotments, bidGroup, s3ObjectName) {
  let bidS3Data = {
    bidMetadata: buildBidEvent(bidMetadata, s3ObjectName),
    bidAllotments: isWithdrawn(bidMetadata.status)
      ? removeBidAllotmentsAwardData(bidAllotments, bidMetadata.bidType)
      : bidAllotments,
    bidGroup: isWithdrawn(bidMetadata.status)
      ? removeBidGroupEmpAwardDataOfTimeOffBid(bidGroup)
      : bidGroup.map((employee) => ({ ...employee, isExpanded: false })),
    modifiedBy: {
      eid: userSession.eid,
      firstName: userSession.firstName,
      lastName: userSession.lastName,
      workgroup: getWorkgroupGivenJobCode(userSession.jobCode),
    },
    referenceNumber: bidMetadata.referenceNumber ? bidMetadata.referenceNumber : "",
  };
  return bidS3Data;
}

export function buildFreedayBidAllotmentsPayload(
  standardDayState,
  reliefDayState,
  calcStandardAllotments,
  calcReliefAllotments
) {
  const filteredStandardAllotments = standardDayState.filter((allotment) => allotment.date !== "");
  const filteredReliefAllotments = reliefDayState.filter((allotment) => allotment.date !== "");
  return {
    standardAllotments: filteredStandardAllotments,
    reliefAllotments: filteredReliefAllotments,
    calcStandardAllotments,
    calcReliefAllotments,
  };
}

const removeBidAllotmentsAwardData = (bidAllotments, bidType) => {
  const clearAwardedEmps = (allotment) => ({
    ...allotment,
    awardedEmps: [],
  });

  if (bidType === BID_TYPE.FREEDAY) {
    const updatedAllotments = {
      standardAllotments: bidAllotments.standardAllotments.map(clearAwardedEmps),
      reliefAllotments: bidAllotments.reliefAllotments.map(clearAwardedEmps),
      calcStandardAllotments: bidAllotments.calcStandardAllotments,
      calcReliefAllotments: bidAllotments.calcReliefAllotments,
    };

    return buildFreedayBidAllotmentsPayload(
      updatedAllotments.standardAllotments,
      updatedAllotments.reliefAllotments,
      updatedAllotments.calcStandardAllotments,
      updatedAllotments.calcReliefAllotments
    );
  } else {
    const updatedAllotments = {
      bidAllotments: bidAllotments.allotments.map(clearAwardedEmps),
      calcDailyAllotments: bidAllotments.calcDailyAllotments,
    };

    return buildAllotmentsPayload(updatedAllotments.bidAllotments, updatedAllotments.calcDailyAllotments);
  }
};

const removeBidGroupEmpAwardDataOfTimeOffBid = (bidGroup) => {
  return bidGroup.map((employee) => {
    const { awardedAllotment, awardedDays, awardType, awardedTimeOffType, awardBsAmPm, ...clearedEmployee } = employee;
    return clearedEmployee;
  });
};

export const buildAllotmentsPayload = (allotmentDayState, calcDailyAllotments) => {
  const filteredAllotments = allotmentDayState.filter((allotment) => allotment.date !== "");
  return {
    allotments: filteredAllotments,
    calcDailyAllotments,
  };
};
